<script setup lang="ts">
import { RouterLink } from 'vue-router'
import Card from '@/components/ui/Card.vue'
import Icon from '@/components/icons/Icon.vue'
import type { AnyCalculatorDefinition } from '@/types/calculator'
import type { IconName } from '@/components/icons/Icon.vue'

defineProps<{
  calc: AnyCalculatorDefinition
  categoryLabel?: string
  categoryIcon?: IconName
}>()
</script>

<template>
  <RouterLink :to="`/${calc.slug}`" class="calc-card">
    <Card padding="md" raised accent="primary" hover>
      <div v-if="categoryLabel" class="calc-card__meta">
        <span v-if="categoryIcon" class="calc-card__icon">
          <Icon :name="categoryIcon" :size="12" />
        </span>
        <span class="calc-card__category">{{ categoryLabel }}</span>
      </div>
      <h3 class="calc-card__title">{{ calc.title }}</h3>
      <p class="calc-card__desc">{{ calc.description }}</p>
      <span class="calc-card__cta"> Calculate <Icon name="arrow-right" :size="14" /> </span>
    </Card>
  </RouterLink>
</template>

<style scoped>
.calc-card {
  text-decoration: none;
  color: inherit;
  display: block;
}

.calc-card__meta {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-3);
}

.calc-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: var(--radius-sm);
  background: linear-gradient(135deg, rgb(10 92 86 / 0.12), rgb(13 148 136 / 0.08));
  color: var(--color-primary);
}

.calc-card__category {
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-full);
  background-color: rgb(10 92 86 / 0.06);
  color: var(--color-primary-dark);
  font-size: var(--text-xs);
  font-weight: var(--font-medium);
}

.calc-card__title {
  font-size: var(--text-lg);
  font-weight: var(--font-semibold);
  color: var(--color-text);
}

.calc-card__desc {
  margin-top: var(--space-2);
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  line-height: var(--leading-relaxed);
}

.calc-card__cta {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  margin-top: var(--space-3);
  font-weight: var(--font-semibold);
  color: var(--color-primary);
  font-size: var(--text-sm);
  transition: gap 0.15s ease;
}

.calc-card:hover .calc-card__cta {
  gap: var(--space-2);
}
</style>
